"use client";

import { componentCls } from "@faire/slate/lib/tracking";
import { Labelled } from "@faire/slate/private/form/Labelled";
import { buildFormIds } from "@faire/slate/private/form/utils";
import type { IText } from "@faire/slate/private/shared/types";
import { HelperWithCharCounter } from "@faire/slate/private/text-area/HelperWithCharCounter";
import { TextAreaBase } from "@faire/slate/private/text-area/TextAreaBase";
import type { ITextAreaInputWebProps } from "@faire/slate/private/text-area/types";
import { isNotUndefined } from "@faire/typescript";
import React, {
  FormEventHandler,
  useCallback,
  useEffect,
  useState,
} from "react";

import { TEXTAREA_UNIQUE_ID_PREFIX } from "./consts";

type IProps = {
  /**
   * The maximum string length that the user can enter.
   * If this value isn't specified, the user can enter an unlimited number of characters.
   *
   * When defined, it shows the character counter
   */
  characterLimit?: number;

  /**
   * Whether the text area component is disabled.
   */
  disabled?: boolean;

  /**
   * Controls the error state of the text area component.
   * When error is a string or `<StrictLocalMsg>` or `<ServerSideLocalMsg>`, error content
   * is displayed in the helper text.
   */
  error?: boolean | IText;

  /**
   * The helper text of the text area component.
   */
  helper?: IText;

  /**
   * The label of the text area component.
   */
  label?: string;

  /**
   * Whether to append "(optional)" text to the text area label.
   *
   * @default false
   */
  optional?: boolean;

  /**
   * The placeholder of the text area component.
   */
  placeholder?: string;

  /**
   * Whether the text area component is resizable.
   *
   * @default true
   */
  resize?: boolean;

  /**
   * The tooltip text for the tooltip in the label.
   */
  tooltipText?: IText;

  /**
   * The value of the text area component.
   */
  value?: string;

  /**
   * **web**
   * Specifies the initial visible number of lines in a text area (initial height).
   * The minimum value applied is 2 (values below 2 will be ignored).
   */
  rows?: number;

  // DO NOT ADD any new prop without aligning with Slate team first
} & ITextAreaInputWebProps;

/**
 * A Text Area provides users with a free-form space to enter and edit long strings of text
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * {@link https://www.notion.so/faire/Text-Area-2242efb5c25a80e8a1c0e63fcc5d69f8 | Slate/TextArea}
 */
export const TextArea: React.FC<IProps> = ({
  characterLimit,
  disabled,
  error,
  helper,
  id: idProp,
  label,
  resize = true,
  onInput,
  ref,
  tooltipText,
  value,
  optional = false,
  ...props
}) => {
  const { id, helperId } = buildFormIds({
    prefix: TEXTAREA_UNIQUE_ID_PREFIX,
    idProp,
    name: props.name,
    label,
    helper,
    error,
  });

  const [characterCount, setCharacterCount] = useState(value?.length ?? 0);

  const showHelperSection =
    helper || typeof error === "string" || isNotUndefined(characterLimit);

  const handleInput: FormEventHandler<HTMLTextAreaElement> = useCallback(
    (e) => {
      onInput?.(e);
      setCharacterCount(e.currentTarget.value.length);
    },
    [onInput]
  );

  useEffect(() => {
    if (isNotUndefined(characterLimit) && isNotUndefined(value)) {
      setCharacterCount(value.length);
    }
  }, [characterLimit, value]);

  return (
    <Labelled
      className={componentCls()}
      data-component-name="Slate/TextArea"
      disabled={disabled}
      error={!!error}
      label={label}
      inputId={id}
      tooltipText={tooltipText}
      optional={optional}
    >
      <TextAreaBase
        aria-describedby={helperId}
        disabled={disabled}
        error={error}
        id={id}
        maxLength={characterLimit}
        resize={resize}
        onInput={handleInput}
        ref={ref}
        value={value}
        {...props}
      />
      {showHelperSection ? (
        <>
          <div className="h-1" />
          <HelperWithCharCounter
            characterCount={characterCount}
            characterLimit={characterLimit}
            disabled={disabled}
            error={error}
            helperId={helperId}
          >
            {helper}
          </HelperWithCharCounter>
        </>
      ) : null}
    </Labelled>
  );
};
